// Ping wheel — the canned-message grid. Bolo3K deliberately has NO free-text
// chat: phones send one of these short, era-appropriate lines via the
// ordinary chat wire (c2s.chat), so desktop players just see them as chat —
// cross-play preserved.
//
// Wire-text constraint: the server's chat sanitiser strips '-' along with
// control chars (packages/server/src/room.ts handleChat — the regex
// /[\r\n\t-]/g has a literal trailing dash), so canned messages must not
// rely on hyphens. No emoji in wire text either (desktop font/table vibe).

import type { ChatScope } from '@bolo2k/shared';
import type { GameContext } from '../context.js';

/** The canned vocabulary. Short imperatives in Bolo's voice. */
export const CANNED_MESSAGES: readonly string[] = [
  'Attack!',
  'Defend the base!',
  'Help!',
  'On my way',
  'Take the pillbox',
  'Retreat!',
  'Nice shot',
  'Form alliance?',
];

export interface PingWheel {
  readonly el: HTMLElement;
  /** Re-read ctx.state(): enables/disables the Allies scope (needs at least
   *  one active mutual pact) and the send buttons (spectators can't chat —
   *  the server drops their messages silently). Called on open and on a
   *  slow poll while the sheet is visible. */
  refresh(): void;
}

/** True when the local player has at least one active mutual alliance.
 *  Same bitmap read the desktop alliance panel uses: state.allies[me] is
 *  the server-derived active-pact bitmap; mask out the self bit
 *  defensively even though the sim never sets it. */
function hasActiveAllies(ctx: GameContext): boolean {
  const state = ctx.state();
  if (!state || state.myOwner < 0) return false;
  const active = (state.allies[state.myOwner] ?? 0) & ~(1 << state.myOwner);
  return active !== 0;
}

export function createPingWheel(ctx: GameContext, onSent: () => void): PingWheel {
  const el = document.createElement('div');
  el.className = 'cw-ping';

  let scope: ChatScope = 'all';

  // Scope toggle — segmented All | Allies. Allies is only selectable while
  // an active pact exists; if the last alliance dissolves mid-open, refresh
  // snaps the scope back to 'all'.
  const scopeRow = document.createElement('div');
  scopeRow.className = 'cw-scope';
  const scopeLabel = document.createElement('span');
  scopeLabel.className = 'cw-scope-label';
  scopeLabel.textContent = 'SEND TO';
  const allBtn = document.createElement('button');
  allBtn.type = 'button';
  allBtn.className = 'cw-scope-btn cw-on';
  allBtn.textContent = 'ALL';
  const alliesBtn = document.createElement('button');
  alliesBtn.type = 'button';
  alliesBtn.className = 'cw-scope-btn';
  alliesBtn.textContent = 'ALLIES';
  scopeRow.append(scopeLabel, allBtn, alliesBtn);
  el.appendChild(scopeRow);

  function setScope(next: ChatScope): void {
    scope = next;
    allBtn.classList.toggle('cw-on', scope === 'all');
    alliesBtn.classList.toggle('cw-on', scope === 'allies');
  }
  allBtn.addEventListener('click', () => setScope('all'));
  alliesBtn.addEventListener('click', () => {
    if (!alliesBtn.disabled) setScope('allies');
  });

  const grid = document.createElement('div');
  grid.className = 'cw-grid';
  el.appendChild(grid);

  const pingButtons: HTMLButtonElement[] = [];
  for (const text of CANNED_MESSAGES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cw-ping-btn';
    btn.textContent = text;
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      ctx.sendChat(text, scope);
      onSent(); // dismiss-on-action; the server echoes the line back to us
    });
    grid.appendChild(btn);
    pingButtons.push(btn);
  }

  function refresh(): void {
    const state = ctx.state();
    const spectating = !state || state.myOwner < 0;
    for (const b of pingButtons) b.disabled = spectating;
    const allies = hasActiveAllies(ctx);
    alliesBtn.disabled = !allies;
    if (!allies && scope === 'allies') setScope('all');
  }
  refresh();

  return { el, refresh };
}
